// Mirrors transaction's bill cycle rules: day/week/month span billCycleInterval units, month_segment splits a month into billCycleInterval periods.

export type BillCycleType = "month_segment" | "day" | "week" | "month";

export const BILL_CYCLE_TYPE_OPTIONS: {
	value: BillCycleType;
	label: string;
}[] = [
	{ value: "month_segment", label: "Month segment (periods per month)" },
	{ value: "day", label: "Daily" },
	{ value: "week", label: "Weekly" },
	{ value: "month", label: "Monthly" },
];

const UNIT_LABELS: Record<Exclude<BillCycleType, "month_segment">, string> = {
	day: "day",
	week: "week",
	month: "month",
};

// The latest day a month segment may be cut, so every period exists in a 28-day February.
const MAX_MONTH_SEGMENT_CUT = 28;

export const isBillCycleType = (value: unknown): value is BillCycleType =>
	BILL_CYCLE_TYPE_OPTIONS.some((option) => option.value === value);

// month_segment needs at least two periods per month; the other units start at one.
export const minBillCycleInterval = (type: BillCycleType) =>
	type === "month_segment" ? 2 : 1;

// Cut-off day bounds per type; day / week ignore the cut-off day entirely.
export const cutoffDayRange = (
	type: BillCycleType,
	interval: number,
): { min: number; max: number } | null => {
	if (type === "month_segment") {
		const cuts = Math.max(interval - 1, 1);
		return { min: 1, max: Math.floor(MAX_MONTH_SEGMENT_CUT / cuts) };
	}
	if (type === "month") return { min: 1, max: 31 };
	return null;
};

export const usesCutoffDay = (type: BillCycleType) =>
	type === "month_segment" || type === "month";

// Month segments follow the calendar, so only day/week/month chain from a start date.
export const usesStartDate = (type: BillCycleType) => type !== "month_segment";

// "2026-11-01T00:00:00Z" -> "2026-11-01"; the column is a date, so never shift by timezone.
export const toDateInputValue = (value?: string | null) =>
	value ? value.slice(0, 10) : "";

// "2026-11-01" -> "2026-11-01T00:00:00Z" for the Time scalar.
export const fromDateInputValue = (value: string) =>
	value ? `${value}T00:00:00Z` : null;

const cutoffLabel = (cutoffDay: number) =>
	cutoffDay >= 29 ? "end of month" : `day ${cutoffDay}`;

// Periods of one month cut every cutoffDay days, e.g. 3 x 10 -> ["1–10", "11–20", "21–end of month"].
export const monthSegmentRanges = (periods: number, cutoffDay: number) =>
	Array.from({ length: periods }, (_, index) =>
		index === periods - 1
			? `${index * cutoffDay + 1}–end of month`
			: `${index * cutoffDay + 1}–${(index + 1) * cutoffDay}`,
	);

// Human summary for the config list, e.g. "3× a month (1–10, 11–20, 21–end of month)".
export const describeBillCycle = (config: {
	billCycleType?: string | null;
	billCycleInterval?: number | null;
	cutoffDay?: number | null;
}) => {
	const type = isBillCycleType(config.billCycleType)
		? config.billCycleType
		: "month_segment";
	const cutoffDay = config.cutoffDay ?? 15;
	if (type === "month_segment") {
		const periods = Math.max(config.billCycleInterval ?? 2, 2);
		const times = periods === 2 ? "Twice a month" : `${periods}× a month`;
		const ranges =
			periods <= 4
				? monthSegmentRanges(periods, cutoffDay).join(", ")
				: `every ${cutoffDay} days, last to end of month`;
		return `${times} (${ranges})`;
	}
	const interval = Math.max(config.billCycleInterval ?? 1, 1);
	const unit = UNIT_LABELS[type];
	const every = interval === 1 ? `Every ${unit}` : `Every ${interval} ${unit}s`;
	return type === "month"
		? `${every} (cut-off ${cutoffLabel(cutoffDay)})`
		: every;
};

export type BillCycleFormValues = {
	billCycleType: BillCycleType;
	billCycleInterval: number;
	billCycleStartDate: string;
	cutoffDay: number;
	paymentDueDays: number;
	graceDays: number;
};

// Same checks the backend runs (validateBillCycle), so the dialog fails before the round trip.
export const validateBillCycleForm = (values: BillCycleFormValues) => {
	const errors: Partial<Record<keyof BillCycleFormValues, string>> = {};
	const minInterval = minBillCycleInterval(values.billCycleType);
	if (
		!Number.isInteger(values.billCycleInterval) ||
		values.billCycleInterval < minInterval
	) {
		errors.billCycleInterval =
			values.billCycleType === "month_segment"
				? "A month segment cycle needs at least 2 periods per month."
				: "Interval must be at least 1.";
	}
	const range = cutoffDayRange(values.billCycleType, values.billCycleInterval);
	if (
		range &&
		(!Number.isInteger(values.cutoffDay) ||
			values.cutoffDay < range.min ||
			values.cutoffDay > range.max)
	) {
		errors.cutoffDay =
			values.billCycleType === "month"
				? "Cut-off day must be 1–31 (29–31 = end of month)."
				: `Cut-off day must be 1–${range.max} so every period fits in February.`;
	}
	if (!Number.isInteger(values.paymentDueDays) || values.paymentDueDays < 0) {
		errors.paymentDueDays = "Payment due days cannot be negative.";
	}
	if (!Number.isInteger(values.graceDays) || values.graceDays < 0) {
		errors.graceDays = "Grace days cannot be negative.";
	}
	return errors;
};

// Form values for a stored config, or the default cycle (twice a month, cut-off 15, grace 0 as in migration 008) when creating.
export const initialBillCycleValues = (config?: {
	billCycleType?: string | null;
	billCycleInterval?: number | null;
	billCycleStartDate?: string | null;
	cutoffDay?: number | null;
	paymentDueDays?: number | null;
	graceDays?: number | null;
}): BillCycleFormValues => ({
	billCycleType: isBillCycleType(config?.billCycleType)
		? config.billCycleType
		: "month_segment",
	billCycleInterval: config?.billCycleInterval ?? 2,
	billCycleStartDate: toDateInputValue(config?.billCycleStartDate),
	cutoffDay: config?.cutoffDay ?? 15,
	paymentDueDays: config?.paymentDueDays ?? 7,
	graceDays: config?.graceDays ?? 0,
});

// Shared fields of the create and update inputs; day/week keep the stored cut-off day as-is.
export const toBillCycleInput = (
	values: BillCycleFormValues & { isActive: boolean },
) => ({
	cutoffDay: Number.isInteger(values.cutoffDay) ? values.cutoffDay : 15,
	paymentDueDays: values.paymentDueDays,
	graceDays: values.graceDays,
	billCycleType: values.billCycleType,
	billCycleInterval: values.billCycleInterval,
	billCycleStartDate: usesStartDate(values.billCycleType)
		? fromDateInputValue(values.billCycleStartDate)
		: null,
	isActive: values.isActive,
});
